"use client"

import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import Dashboard from "../../../layout/Dashboard"
import { FiscalForm } from "../Forms"
import { Select, PageLoader } from "../../../components"
import { Api, getSession } from "../../../api/Assinafy"
import "../FiscalData/styles.scss"

const ACCOUNT_TYPE_OPTIONS = [
  { value: "pf", label: "Pessoa Física" },
  { value: "pj", label: "Pessoa Jurídica" },
]

const isCPF = (taxpayerId) => {
  if (!taxpayerId) return true
  return taxpayerId.replace(/\D/g, "").length === 11
}

const formatCPF = (cpf) => {
  if (!cpf) return ""
  const cleanCPF = cpf.replace(/\D/g, "")
  return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
}

const formatCNPJ = (cnpj) => {
  if (!cnpj) return ""
  const cleanCNPJ = cnpj.replace(/\D/g, "")
  return cleanCNPJ.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
}

const formatCEP = (cep) => {
  if (!cep) return ""
  const cleanCEP = cep.replace(/\D/g, "")
  return cleanCEP.replace(/(\d{5})(\d{3})/, "$1-$2")
}

const AccountIdentification = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const requestedReturnTo = location.state?.returnTo
  const returnTo = typeof requestedReturnTo === "string" && requestedReturnTo.startsWith("/dashboard/")
    ? requestedReturnTo
    : null

  const [accountType, setAccountType] = useState("pf")
  const [customerData, setCustomerData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await Api("customer/view")()

        if (response?.status === 200 && response?.data?.data) {
          const data = response.data.data
          setCustomerData(data)
          setAccountType(isCPF(data.taxpayer_id) ? "pf" : "pj")
        }
      } catch (err) {
        console.error("Erro ao buscar dados do customer:", err)
        setError(err.message || "Erro ao carregar dados da conta")
      } finally {
        setLoading(false)
      }
    }

    fetchCustomerData()
  }, [])

  const getFormDefaults = () => {
    const sessionUser = getSession()?.user || {}
    const isPF = accountType === "pf"

    // Tipo já cadastrado no customer (se houver). Só reaproveitamos os dados
    // salvos quando o tipo selecionado é o mesmo do cadastro. Ao trocar de tipo
    // (ex.: PF -> PJ) os campos vêm vazios: os dados salvos são os pessoais do
    // responsável e, para PJ, o CNPJ preenche os dados da empresa automaticamente.
    const savedType = customerData ? (isCPF(customerData.taxpayer_id) ? "pf" : "pj") : null

    if (!customerData || savedType !== accountType) {
      if (isPF) {
        return {
          name: sessionUser.name || "",
          email: sessionUser.email || "",
          phone: sessionUser.telephone || sessionUser.phone_number || "",
        }
      }
      return {}
    }

    return {
      name: customerData.name || sessionUser.name || "",
      cpf: isPF ? formatCPF(customerData.taxpayer_id) : "",
      cnpj: !isPF ? formatCNPJ(customerData.taxpayer_id) : "",
      cep: formatCEP(customerData.address?.postal_code) || "",
      city: customerData.address?.city || "",
      state: customerData.address?.state || "",
      address: customerData.address?.line1 || "",
      number: customerData.address?.house_number || "",
      neighborhood: customerData.address?.neighborhood || "",
      email: customerData.email || sessionUser.email || "",
      phone: customerData.phone_number || sessionUser.telephone || sessionUser.phone_number || "",
    }
  }

  const handleSaveSuccess = async () => {
    // O pending action "missing_billing_profile" é resolvido pela própria API
    // após os dados do customer serem preenchidos — não o resolvemos aqui.
    sessionStorage.removeItem("pendingActionsChecked")
    if (returnTo) {
      navigate(returnTo, {
        replace: true,
        state: location.state?.returnState,
      })
      return
    }

    navigate("/dashboard", {
      replace: true,
      state: { showBillingProfileSuccess: true },
    })
  }

  if (loading) {
    return <PageLoader />
  }

  if (error) {
    return (
      <Dashboard.Content style={{ maxWidth: "608px" }}>
        <Dashboard.Header title="Identificação da conta" className="d-flex reverse-row" />
        <div className="alert alert-danger">
          <p>{error}</p>
          <button className="btn btn-primary" type="button" onClick={() => window.location.reload()}>
            Tentar novamente
          </button>
        </div>
      </Dashboard.Content>
    )
  }

  return (
    <Dashboard.Content style={{ maxWidth: "608px" }}>
      <Dashboard.Header title="Identificação da conta" className="d-flex reverse-row" />
      <p className="fiscalData-info">Informe abaixo o responsável por essa conta do Assinafy.</p>

      <div className="fiscalData-account-type">
        <Select
          label="Tipo de Conta"
          options={ACCOUNT_TYPE_OPTIONS}
          value={accountType}
          onChange={(event) => setAccountType(event.target.value)}
        />
      </div>

      <FiscalForm
        key={accountType}
        cpf={accountType === "pf"}
        defaults={getFormDefaults()}
        mode="identification"
        saveButtonLabel="Salvar"
        onSaveSuccess={handleSaveSuccess}
      />
    </Dashboard.Content>
  )
}

export default AccountIdentification
